import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const DISMISSED_KEY = 'setup_prompts_dismissed';

interface DismissedState {
  discord?: boolean;
  password?: boolean;
  dismissedAt?: string;
}

/**
 * Shows banner notifications for users who need to complete setup:
 * - Set a password (if they don't have one)
 *
 * Note: Discord linking has its own dedicated MigrationDiscordBanner.
 * This component handles only the password setup prompt.
 */
export const SetupPromptBanner: React.FC = () => {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState<DismissedState>({});

  // Load dismissed state from sessionStorage
  useEffect(() => {
    const stored = sessionStorage.getItem(DISMISSED_KEY);
    if (stored) {
      try {
        setDismissed(JSON.parse(stored));
      } catch {
        // Invalid JSON, ignore
      }
    }
  }, []);

  const dismissPrompt = (type: 'password'): void => {
    const newDismissed = {
      ...dismissed,
      [type]: true,
      dismissedAt: new Date().toISOString(),
    };
    setDismissed(newDismissed);
    sessionStorage.setItem(DISMISSED_KEY, JSON.stringify(newDismissed));
  };

  if (!user?.feature_flags?.self_service_account) {
    return null;
  }

  const needsPassword = user?.has_password === false && !dismissed.password;

  if (!needsPassword) {
    return null;
  }

  return (
    <div className="bg-accent-yellow bg-opacity-10 border-b border-accent-yellow px-4 py-2 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <svg className="w-4 h-4 text-accent-yellow flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
        <span className="text-sm text-gray-300">
          Set your password to log in directly.
        </span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Link
          to="/profile?tab=account"
          className="px-3 py-1 text-sm font-medium bg-accent-yellow hover:bg-accent-yellow text-dark-bg rounded transition-colors"
        >
          Set Password
        </Link>
        <button
          onClick={() => dismissPrompt('password')}
          className="text-gray-500 hover:text-gray-400 transition-colors"
          aria-label="Dismiss"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};
